import { nanoid } from 'nanoid';

import { SpeechSuperCoreTypeEnum } from '../../constants/speechsuper.constants.ts';

import { SpeechSuperConnection } from './speechsuper-connection.ts';

/**
 * Manages a pool of connections for a specific core type.
 * Maintains separate pools for idle and active connections.
 */
export class SpeechSuperConnectionPool {
  // Instance properties
  private idleConnections = new Map<string, SpeechSuperConnection>();
  private activeConnections = new Map<string, SpeechSuperConnection>();

  constructor(private readonly coreType: SpeechSuperCoreTypeEnum) {}

  /**
   * Gets an available connection from the idle pool.
   * Moves the connection to the active pool if found.
   * @returns An idle connection if available, null otherwise
   */
  getIdleConnection(): SpeechSuperConnection | null {
    const connection = this.idleConnections.values().next().value;
    if (connection) {
      this.activeConnections.set(connection.id, connection);
      this.idleConnections.delete(connection.id);
    }
    return connection ?? null;
  }

  /**
   * Creates a new connection and adds it to the active pool.
   * @returns A new active connection
   */
  createConnection(): SpeechSuperConnection {
    const connection = new SpeechSuperConnection(this.generateConnectionId(), this.coreType, this);
    this.activeConnections.set(connection.id, connection);
    return connection;
  }

  /**
   * Releases a connection from the active pool back to the idle pool.
   * @param connectionId - The ID of the connection to release
   */
  releaseConnection(connectionId: string): void {
    const connection = this.activeConnections.get(connectionId);
    if (connection) {
      this.idleConnections.set(connection.id, connection);
      this.activeConnections.delete(connectionId);
    }
  }

  /**
   * Gets a connection by ID from either the active or idle pool.
   * @param connectionId - The ID of the connection to retrieve
   * @returns The connection if found, null otherwise
   */
  getConnection(connectionId: string): SpeechSuperConnection | null {
    return this.activeConnections.get(connectionId) ?? this.idleConnections.get(connectionId) ?? null;
  }

  /**
   * Removes expired connections from the idle pool.
   * @param maxIdleTime - Maximum time in milliseconds a connection can be idle
   * @returns Array of expired connections that were removed
   */
  cleanupExpiredConnections(maxIdleTime: number): SpeechSuperConnection[] {
    const now = Date.now();
    const expiredConnections: SpeechSuperConnection[] = [];

    this.idleConnections.forEach((conn) => {
      if (now - conn.getLastUsedTime() >= maxIdleTime) {
        expiredConnections.push(conn);
        this.idleConnections.delete(conn.id);
      }
    });

    return expiredConnections;
  }

  /**
   * Closes all connections and clears both pools.
   * Should be called when the pool is no longer needed.
   */
  async cleanUp(): Promise<void> {
    await Promise.all([
      ...Array.from(this.activeConnections.values()).map((conn) => conn.close()),
      ...Array.from(this.idleConnections.values()).map((conn) => conn.close()),
    ]);
    this.idleConnections.clear();
    this.activeConnections.clear();
  }

  /**
   * Removes a connection from either pool.
   * @param connectionId - The ID of the connection to remove
   */
  removeConnection(connectionId: string): void {
    if (this.activeConnections.has(connectionId)) {
      this.activeConnections.delete(connectionId);
    }

    if (this.idleConnections.has(connectionId)) {
      this.idleConnections.delete(connectionId);
    }
  }

  private generateConnectionId(): string {
    return nanoid();
  }
}
